import React, { Fragment } from "react";
import PhoneInput from "../../PhoneInput";
import * as HeadlessUI from "@headlessui/react";
import { Plus, X, FileText, ImageIcon } from "lucide-react";

/**
 * Reusable form field component for exhibitor requests
 * Handles text, email, textarea, dropdown, checkbox, phone, and file/image types
 */
const ExhibitorReqFormField = ({ 
  field, 
  value, 
  onChange, 
  error, 
  onFileChange,
  disabled = false
}) => {
  const type = (field.field_type || field.type || "text").toLowerCase();
  const options = typeof field.options === "string" 
    ? JSON.parse(field.options) 
    : field.options;
  
  const hasError = !!error;
  const baseInputCls = `${hasError
    ? "border-red-400 ring-2 ring-red-400/20"
    : "border-gray-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"} ${disabled ? "bg-gray-100 opacity-70 cursor-not-allowed" : "bg-gray-50"}`;

  const renderField = () => {
    switch (type) {
      case "textarea":
        return (
          <textarea
            placeholder={`Enter ${field.label}`}
            value={value || ""}
            disabled={disabled}
            onChange={(e) => !disabled && onChange(field.id, e.target.value)}
            className={`w-full h-28 p-4 border rounded-lg outline-none text-sm transition-all ${baseInputCls}`}
          />
        );

      case "select":
      case "dropdown":
        const selectedValue = value || "";
        return (
          <HeadlessUI.Listbox
            value={selectedValue}
            disabled={disabled}
            onChange={(val) => !disabled && onChange(field.id, val)}
          >
            {({ open }) => (
              <div className="relative">
                <HeadlessUI.ListboxButton
                  className={`w-full h-10 px-3.5 flex items-center justify-between border rounded-lg outline-none text-sm transition-all duration-200 ${
                    open ? "border-indigo-500 ring-2 ring-indigo-500/10 bg-white" : baseInputCls
                  }`}
                >
                  <span className={`block truncate font-medium ${!selectedValue ? "text-gray-400" : "text-gray-900 font-bold"}`}>
                    {selectedValue || `Select ${field.label}`}
                  </span>
                  <div className="flex items-center gap-1.5 text-gray-400">
                    {selectedValue && (
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse mr-1"></div>
                    )}
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </HeadlessUI.ListboxButton>

                <HeadlessUI.Transition
                  show={open}
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <HeadlessUI.ListboxOptions className="absolute mt-1.5 w-full bg-white border border-gray-100 rounded-xl shadow-xl z-[60] overflow-hidden max-h-60 overflow-y-auto py-1 focus:outline-none custom-scrollbar">
                    {options?.map((opt) => (
                      <HeadlessUI.ListboxOption
                        key={opt}
                        value={opt}
                        className={({ active, selected }) =>
                          `group relative cursor-pointer select-none py-2.5 px-3 mx-1.5 my-0.5 rounded-lg transition-all duration-200 ${
                            active
                              ? "bg-indigo-50 text-indigo-700"
                              : selected
                                ? "bg-indigo-50/50 text-indigo-600"
                                : "text-gray-600 hover:bg-gray-50/50"
                          }`
                        }
                      >
                        {({ selected }) => (
                          <div className="flex items-center justify-between">
                            <span className={`block truncate text-sm transition-all ${
                              selected ? "font-bold scale-105" : "font-medium"
                            }`}>
                              {opt}
                            </span>
                            {selected && (
                              <div className="flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500"></div>
                                <svg className="w-3.5 h-3.5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </div>
                        )}
                      </HeadlessUI.ListboxOption>
                    ))}
                  </HeadlessUI.ListboxOptions>
                </HeadlessUI.Transition>
              </div>
            )}
          </HeadlessUI.Listbox>
        );

      case "checkbox":
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-1">
            {options?.map((opt) => {
              const checked = Array.isArray(value) ? value.includes(opt) : value === opt;
              return (
                <label 
                  key={opt} 
                  className={`group relative flex items-center p-3 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    checked 
                      ? "border-indigo-500 bg-indigo-50/40 shadow-sm" 
                      : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/50 shadow-sm hover:shadow-md"
                  }`}
                >
                  <div className="relative flex items-center">
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={disabled}
                      onChange={(e) => {
                        if (disabled) return;
                        let newValue;
                        if (Array.isArray(value)) {
                          newValue = e.target.checked 
                            ? [...value, opt] 
                            : value.filter(v => v !== opt);
                        } else {
                          newValue = e.target.checked ? [opt] : [];
                        }
                        onChange(field.id, newValue);
                      }}
                      className="peer h-5 w-5 cursor-pointer appearance-none rounded-lg border-2 border-gray-300 bg-white checked:bg-indigo-500 checked:border-indigo-500 transition-all focus:ring-2 focus:ring-indigo-500/20"
                    />
                    <svg
                      className="absolute h-3.5 w-3.5 pointer-events-none left-0.5.5 opacity-0 peer-checked:opacity-100 text-white transition-opacity duration-200"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth="4"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className={`ml-3 text-sm font-bold transition-colors duration-200 ${
                    checked ? "text-indigo-900" : "text-gray-600 group-hover:text-gray-900"
                  }`}>
                    {opt}
                  </span>
                  {checked && (
                    <div className="absolute right-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse"></div>
                    </div>
                  )}
                </label>
              );
            })}
          </div>
        );

      case "phone":
        return (
          <div className={`phone-input-container ${hasError ? "has-error" : ""}`}>
            <PhoneInput
              country={"in"}
              preferredCountries={["in"]}
              enableSearch={true}
              value={value || ""}
              disabled={disabled}
              onChange={(val) => !disabled && onChange(field.id, val)}
              inputStyle={{
                width: "100%",
                height: "40px",
                fontSize: "14px",
                borderRadius: "8px",
                borderColor: hasError ? "#f87171" : "#e5e7eb",
                backgroundColor: disabled ? "#f3f4f6" : "#f9fafb",
                cursor: disabled ? "not-allowed" : "text"
              }}
              containerStyle={{
                borderRadius: "8px",
                opacity: disabled ? 0.7 : 1
              }}
              buttonStyle={{
                borderRadius: "8px 0 0 8px",
                borderColor: hasError ? "#f87171" : "#e5e7eb",
                backgroundColor: disabled ? "#f3f4f6" : "#f9fafb",
                cursor: disabled ? "not-allowed" : "pointer"
              }}
            />
          </div>
        );

      case "image":
        const imageUrl = options?.[0];
        return (
          <div className="w-full">
            {imageUrl ? (
              <div className="w-full border rounded-xl overflow-hidden bg-white shadow-sm flex items-center justify-center p-2 group transition-all hover:shadow-md">
                <img
                  src={imageUrl}
                  alt={field.label}
                  className="max-w-full h-auto max-h-72 object-contain rounded-lg shadow-sm"
                />
              </div>
            ) : (
              <div className="p-10 border-2 border-dashed border-gray-100 rounded-xl bg-gray-50/50 flex flex-col items-center justify-center text-gray-400 font-medium">
                <ImageIcon size={32} className="mb-2 opacity-20" />
                <span className="text-sm">No image available</span>
              </div>
            )}
          </div>
        );

      case "file":
        const isImage = (field.name === "company_logo" || field.field_name === "company_logo" || field.type === "image" || field.field_type === "image") ||
          (typeof value === "string" && (value.match(/\.(jpeg|jpg|gif|png|webp|svg)($|\?)/i) || value.includes("uploads/") || value.startsWith("http"))) ||
          (value instanceof File && value.type?.startsWith("image/"));
        const previewUrl = value instanceof File ? URL.createObjectURL(value) : (typeof value === "string" ? value : null);

        return (
          <div
            className={`border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center transition-colors relative group ${
              disabled ? "bg-gray-100 border-gray-100 opacity-60 cursor-not-allowed" :
              hasError
                ? "border-red-400 bg-red-50/10 cursor-pointer"
                : "border-gray-200 bg-gray-50 hover:bg-gray-100/60 cursor-pointer"
            }`}
            onClick={() => !disabled && document.getElementById(`add_file_${field.id}`).click()}
          >
            <input
              type="file"
              id={`add_file_${field.id}`}
              className="hidden"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              onChange={(e) => onFileChange(field.id, e.target.files?.[0])}
            />
            {value ? (
              isImage && previewUrl ? (
                <div className="flex flex-col items-center w-full">
                  <div className="w-full max-h-48 border rounded-lg overflow-hidden bg-white flex items-center justify-center p-2 mb-2 shadow-sm">
                    <img src={previewUrl} alt={field.label} className="max-w-full h-auto max-h-36 object-contain rounded-md" />
                  </div>
                  <span className="text-xs text-indigo-600 font-semibold group-hover:underline">
                    Click to change {field.label}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-500 mb-3 shadow-inner">
                    <FileText size={24} />
                  </div>
                  <span className="text-sm font-semibold text-gray-800 truncate max-w-[200px]">
                    {typeof value === "string" ? value.split("/").pop() : value.name}
                  </span>
                  <span className="text-xs text-indigo-500 font-medium mt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Click to change file
                  </span>
                </div>
              )
            ) : (
              <>
                <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-3 transition-colors ${
                  hasError ? "bg-red-100 text-red-500" : "bg-indigo-50 text-indigo-500 group-hover:bg-indigo-100"
                }`}>
                  <Plus size={24} />
                </div>
                <div className="text-center">
                  <span className={`text-sm font-bold block ${hasError ? "text-red-500" : "text-gray-700"}`}>
                    Upload {field.label}
                  </span>
                  <span className="text-xs text-gray-400 mt-1 block font-medium">
                    Max size: 5MB (PDF, PNG, JPG, JPEG, WEBP)
                  </span>
                </div>
              </>
            )}
          </div>
        ); 

      default:
        return (
          <input
            type={
              type === "email" 
                ? "email" 
                : type === "website" || type === "url" || type === "website_url"
                  ? "url"
                  : type === "date"
                    ? "date"
                    : type === "number"
                      ? "number"
                      : "text"
            }
            placeholder={`Enter ${field.label}`}
            value={value || ""}
            disabled={disabled}
            onChange={(e) => !disabled && onChange(field.id, e.target.value)}
            className={`w-full h-10 px-3.5 border rounded-lg outline-none text-sm transition-all ${baseInputCls}`}
          />
        );
    }
  };

  return (
    <div className="space-y-1.5 w-full">
      <label className="text-sm font-bold text-gray-800 flex items-center gap-1.5 ml-0.5">
        {field.label}
        {field.required && (
          <span className="text-red-500 text-lg leading-none">*</span>
        )}
      </label>
      {renderField()}
      {hasError && (
        <p className="text-xs font-semibold text-red-500 mt-1 pl-1 flex items-center gap-1 animate-in fade-in slide-in-from-left-1">
          <span className="w-1 h-1 rounded-full bg-red-500"></span>
          {error}
        </p>
      )}
    </div>
  );
};

export default ExhibitorReqFormField;
